<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation, Pagination } from 'swiper';
import LandingTitleLayout from '../LandingTitleLayout.vue';
import { LeftArrowIcon, RightArrowIcon } from '@glow/shared/components/icons';
import { breakpoints } from '@glow/shared/lib/breakpoints';
import ReviewVideo from '../ReviewVideo.vue';

type TextReview = {
  id: number;
  reviewerName: string;
  reviewerAvatarUrl: string;
  text: string;
  originalReviewPhotoUrl: string;
};
type VideoReview = {
  id: number;
  reviewerName: string;
  videoUrl: string;
};

const { staticUrl } = useRuntimeConfig().public;
const textReviews: TextReview[] = [
  {
    id: 1,
    reviewerName: 'Василиса Николаева',
    reviewerAvatarUrl: `${staticUrl}/landing/reviews/1/avatar.jpg`,
    text: 'Жутко раздражало, когда нужно точно знать время, когда мастер тебя примет на ногти, а добиться правды не так просто. Долгие ответы просто выводили из себя!!! Я не могла спланировать свой день, находилась в подвешенном состоянии. Особенно ужасно, когда запишешься, а мастер потом отменит по непонятной причине. Девочки поймут... Я безумно рада, что теперь знакома с глоу, никаких нервов - зашел, записался и счастлив! Мастер тебя ждет, а отменить можешь сам, если потребуется!',
    originalReviewPhotoUrl: `${staticUrl}/landing/reviews/1/original.png`,
  },
  {
    id: 2,
    reviewerName: 'Ксения Орлова',
    reviewerAvatarUrl: `${staticUrl}/landing/reviews/2/avatar.png`,
    text: 'GLOW – чудесный сервис, и этим отзывом я хочу выразить благодарность. Спасибо за то, что теперь я не трачу просто кучу времени на поиск мастера. И не переживаю, что мастер может навредить. Сервис вызывает доверие, и когда ты сидишь, смотришь профиль мастера, видишь его отзывы, то понимаешь – этот человек не сделает тебе плохо. Благодаря вам я научилась доверять мастерам🖤',
    originalReviewPhotoUrl: `${staticUrl}/landing/reviews/2/original.png`,
  },
  {
    id: 3,
    reviewerName: 'Наталья Абашева',
    reviewerAvatarUrl: `${staticUrl}/landing/reviews/3/avatar.png`,
    text: 'Спасибо ГЛОУ :) Добавлю и своих пару слов. Пользоваться сервисом безумно удобно. Вместо старого привычного поиска мастера «по сарафанному радио» у меня теперь все просто: зашла, нашла, посмотрела свободное окно – записалась. Все! Я человек нервный и до этого поиск мастера был для меня чем-то таким, чем я не хотела заниматься от слова «совсем». Теперь же все наоборот!) Еще раз спасибо',
    originalReviewPhotoUrl: `${staticUrl}/landing/reviews/3/original.png`,
  },
];

const videoReviews: VideoReview[] = [
  {
    id: 1,
    reviewerName: 'Елена',
    videoUrl: `${staticUrl}/landing/videos/client/1.mov`,
  },
  {
    id: 2,
    reviewerName: 'Оксана',
    videoUrl: `${staticUrl}/landing/videos/client/2.mov`,
  },
  {
    id: 3,
    reviewerName: 'Ольга',
    videoUrl: `${staticUrl}/landing/videos/client/3.mp4`,
  },
  {
    id: 4,
    reviewerName: 'Милена',
    videoUrl: `${staticUrl}/landing/videos/client/4.mp4`,
  },
  {
    id: 5,
    reviewerName: 'Дарина',
    videoUrl: `${staticUrl}/landing/videos/client/5.mp4`,
  },
];

const selectedReviewType = ref<'text' | 'video'>('text');
const lightboxImageUrl = ref<string>();
const playingVideoId = ref<number>();
</script>

<template>
  <div id="reviews" class="container">
    <LandingTitleLayout>
      <template #title>Отзывы клиентов</template>
    </LandingTitleLayout>

    <div class="switcher d-flex">
      <AppButton
        :variant="selectedReviewType === 'text' ? 'primary' : 'secondary'"
        fluid
        @click="selectedReviewType = 'text'"
      >
        Отзывы {{ textReviews.length }}
      </AppButton>
      <AppButton
        :variant="selectedReviewType === 'video' ? 'primary' : 'secondary'"
        fluid
        @click="selectedReviewType = 'video'"
      >
        Видео {{ videoReviews.length }}
      </AppButton>
    </div>

    <template v-if="selectedReviewType === 'text'">
      <Swiper
        :modules="[Navigation, Pagination]"
        :navigation="{
          prevEl: '.slider__prev',
          nextEl: '.slider__next',
        }"
        pagination
        :breakpoints="{
          [breakpoints['xs']]: {
            slidesPerView: 1,
            spaceBetween: 12,
          },
          [breakpoints['md']]: {
            slidesPerView: 2,
            spaceBetween: 32,
          },
          [breakpoints['xl']]: {
            slidesPerView: 3,
            spaceBetween: 32,
          },
        }"
        auto-height
        class="slider"
      >
        <SwiperSlide
          v-for="review in textReviews"
          :key="review.id"
          class="slide"
        >
          <div class="d-flex align-items-center gap-3">
            <AppAvatar
              :name="review.reviewerName"
              :src="review.reviewerAvatarUrl"
            />
            <div>
              <div class="name mb-1">{{ review.reviewerName }}</div>
              <div class="subject">Довольный пользователь</div>
            </div>
          </div>
          <div class="text">
            {{ review.text }}
          </div>
          <AppButton
            class="show-original"
            variant="link"
            @click="lightboxImageUrl = review.originalReviewPhotoUrl"
          >
            Посмотреть оригинал
          </AppButton>
        </SwiperSlide>

        <div class="slider__prev"><LeftArrowIcon /></div>
        <div class="slider__next"><RightArrowIcon /></div>
      </Swiper>
    </template>

    <template v-if="selectedReviewType === 'video'">
      <Swiper
        :modules="[Navigation, Pagination]"
        :navigation="{
          prevEl: '.slider__prev',
          nextEl: '.slider__next',
        }"
        pagination
        :breakpoints="{
          [breakpoints['xs']]: {
            slidesPerView: 1,
            spaceBetween: 12,
          },
          [breakpoints['md']]: {
            slidesPerView: 2,
            spaceBetween: 32,
          },
          [breakpoints['xl']]: {
            slidesPerView: 3,
            spaceBetween: 32,
          },
        }"
        auto-height
        class="slider"
      >
        <SwiperSlide
          v-for="review in videoReviews"
          :key="review.id"
          class="slide slide_video"
        >
          <div class="d-flex align-items-center gap-3">
            <!-- <app-avatar :src="avatar" /> -->
            <div>
              <div class="name mb-1">{{ review.reviewerName }}</div>
              <div class="subject">Довольный пользователь</div>
            </div>
          </div>
          <ReviewVideo
            class="slide_video__video"
            :src="review.videoUrl"
            :is-playing="playingVideoId === review.id"
            @play="playingVideoId = review.id"
            @stop="playingVideoId = undefined"
          />
        </SwiperSlide>

        <div class="slider__prev"><LeftArrowIcon /></div>
        <div class="slider__next"><RightArrowIcon /></div>
      </Swiper>
    </template>
    <AppLightbox
      :model-value="lightboxImageUrl !== undefined"
      :image-urls="lightboxImageUrl ? [lightboxImageUrl] : undefined"
      @update:model-value="lightboxImageUrl = undefined"
    />
  </div>
</template>

<style lang="scss" scoped>
.switcher {
  margin: 0 auto;
  width: 400px;

  @include media-breakpoint-down(md) {
    width: 100%;
  }
}

.slider {
  height: auto !important;
  padding: 40px 30px;

  &__prev,
  &__next {
    position: absolute;
    top: 50% !important;
    z-index: 9 !important;
    padding: 10px !important;
    color: #fff;
    background: var(--app-gradient-primary) !important;
    border: 3px solid #fff;
    border-radius: 50%;
    cursor: pointer;

    svg {
      width: 28px;
      height: 28px;
    }
  }

  &__prev {
    left: 0;
  }

  &__next {
    right: 0;
  }
}

.slide {
  &_video {
    min-height: 500px;

    @include media-breakpoint-only(xs) {
      min-height: 350px;
    }

    @include media-breakpoint-only(md) {
      min-height: 400px;
    }
  }

  .name {
    font-weight: 600;
    font-size: 24px;
    line-height: 28px;
  }

  .subject {
    font-size: 15px;
    line-height: 22px;
    color: var(--app-color-black-secondary);
  }

  .text {
    flex: 1;
    font-size: 17px;
    line-height: 24px;
  }

  .link {
    font-weight: 600;
  }
}

.spacer {
  flex: 1;
}
</style>

<style lang="scss">
#reviews .slide {
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  padding: 32px;
  background: var(--app-color-white-secondary);
  border-radius: 24px;
}

#reviews .swiper-pagination-bullet-active {
  background-color: var(--app-color-primary) !important;
}
</style>
